// Recurring checklists and their runs (T28, spec F8). Lists are fresh for 30 s (plan §9); the open
// run is fresh for 5 s. Every write answers with the whole run, which replaces the cached copy,
// and refreshes the run lists of that checklist. A 409 re-reads the run so the page shows the
// version someone else saved.
import {
  createChecklistsApi,
  createHttp,
  hasStatus,
  type ChecklistDetail,
  type ChecklistPage,
  type ChecklistResult,
  type ChecklistRunAction,
  type ChecklistRunPage,
  type ChecklistRunStatus,
  type ChecklistRunView,
  type StartChecklistRunInput,
} from '@cis/api-client';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

const checklists = createChecklistsApi(createHttp());

const HTTP_CONFLICT = 409;
const RUN_STALE_MS = 5_000;
/** 14 checklists in the pack: one page holds them all. */
export const CHECKLISTS_PAGE_SIZE = 100;
export const RUNS_PAGE_SIZE = 25;

export const CHECKLISTS_KEY = ['checklists'] as const;
const runsKey = (code: string) => [...CHECKLISTS_KEY, code, 'runs'] as const;
export const runKey = (id: number) => ['checklist-runs', id] as const;

export const checklistListQuery = () =>
  queryOptions({
    queryKey: [...CHECKLISTS_KEY, 'list'] as const,
    queryFn: ({ signal }) => checklists.list({ page: 1, pageSize: CHECKLISTS_PAGE_SIZE }, signal),
  });

export function useChecklists(): UseQueryResult<ChecklistPage> {
  return useQuery(checklistListQuery());
}

export const checklistQuery = (code: string) =>
  queryOptions({
    queryKey: [...CHECKLISTS_KEY, code, 'detail'] as const,
    queryFn: ({ signal }) => checklists.get(code, signal),
  });

export function useChecklist(code: string): UseQueryResult<ChecklistDetail> {
  return useQuery(checklistQuery(code));
}

export interface RunListParams {
  readonly page: number;
  readonly status?: ChecklistRunStatus | undefined;
}

export const runListQuery = (code: string, params: RunListParams) =>
  queryOptions({
    queryKey: [...runsKey(code), 'list', params.page, params.status ?? 'all'] as const,
    queryFn: ({ signal }) =>
      checklists.runs(
        code,
        { page: params.page, pageSize: RUNS_PAGE_SIZE, status: params.status },
        signal,
      ),
  });

export function useRunList(code: string, params: RunListParams): UseQueryResult<ChecklistRunPage> {
  return useQuery({ ...runListQuery(code, params), placeholderData: keepPreviousData });
}

/** The newest run of a checklist (the API lists newest first), for the overview table. */
export function useLatestRun(code: string): UseQueryResult<ChecklistRunPage> {
  return useQuery({
    queryKey: [...runsKey(code), 'latest'] as const,
    queryFn: ({ signal }) => checklists.runs(code, { page: 1, pageSize: 1 }, signal),
  });
}

export const runQuery = (id: number) =>
  queryOptions({
    queryKey: runKey(id),
    queryFn: ({ signal }) => checklists.run(id, signal),
    staleTime: RUN_STALE_MS,
  });

export function useRun(id: number): UseQueryResult<ChecklistRunView> {
  return useQuery(runQuery(id));
}

async function settleRun(
  queryClient: QueryClient,
  id: number,
  run: ChecklistRunView | undefined,
  err: Error | null,
): Promise<void> {
  if (run !== undefined) {
    queryClient.setQueryData(runKey(run.id), run);
    await queryClient.invalidateQueries({ queryKey: runsKey(run.checklist.code) });
  }
  if (err !== null && hasStatus(err, HTTP_CONFLICT)) {
    await queryClient.invalidateQueries({ queryKey: runKey(id) });
  }
}

export interface StartRunWrite {
  readonly input: StartChecklistRunInput;
  /**
   * One key per run the person means to start (spec §8.8): the form keeps it across a double
   * click and a retry after a lost answer, and takes a new one when the input changes or a run
   * was made (review G4-R2).
   */
  readonly idempotencyKey: string;
}

/** Starts a run, or a correcting run of a signed one; resolves with the new run. */
export function useStartRun(
  code: string,
): UseMutationResult<ChecklistRunView, Error, StartRunWrite> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (w: StartRunWrite) => checklists.startRun(code, w.input, w.idempotencyKey),
    onSuccess: async (run) => {
      queryClient.setQueryData(runKey(run.id), run);
      await queryClient.invalidateQueries({ queryKey: runsKey(code) });
      if (run.correctsRunId !== null) {
        await queryClient.invalidateQueries({ queryKey: runKey(run.correctsRunId) });
      }
    },
  });
}

export interface ResultWrite {
  readonly item: number;
  readonly version: number;
  readonly result: ChecklistResult;
  readonly note: string;
}

export function useSaveResult(id: number): UseMutationResult<ChecklistRunView, Error, ResultWrite> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (w: ResultWrite) =>
      checklists.putResult(id, w.item, w.version, { result: w.result, note: w.note }),
    onSettled: (run, err) => settleRun(queryClient, id, run, err),
  });
}

export interface RunActionWrite {
  readonly action: ChecklistRunAction;
  readonly version: number;
}

export function useRunAction(
  id: number,
): UseMutationResult<ChecklistRunView, Error, RunActionWrite> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (w: RunActionWrite) => checklists.act(id, w.action, w.version),
    onSettled: (run, err) => settleRun(queryClient, id, run, err),
  });
}
